import { Operation } from 'src/types/operation';
import { WalletFund, WalletFundCompositionRole } from 'src/types/wallet';

import type { UpdateWalletFundPayload as UpdateWalletFundRequest } from 'src/services/wallet';

export type EditableQuota = {
  id: string;
  class: string;
  series: string | number;
  amount: number;
};

export type EditableWalletFund = Operation & {
  editorId?: string;
  walletFundId?: number;
  parentWalletFundId?: number;
  compositionRole: WalletFundCompositionRole | null;
  useAlertDefault: boolean;
  quotas: EditableQuota[];
};

export type EditableWalletFundNode = EditableWalletFund & {
  nodeId: string;
  isFicParent: boolean;
  children: EditableWalletFundNode[];
};

export type UpdateWalletFundPayload = UpdateWalletFundRequest;

export const DEFAULT_FIC_CHILD_QUOTA = {
  class: 'Não informado',
  series: '1',
  amount: 1,
} as const;

export function getWalletFundNodeId(
  fund: Pick<EditableWalletFund, 'walletFundId' | 'editorId' | 'id'>,
): string {
  return `fund-${fund.walletFundId ?? fund.editorId ?? fund.id}`;
}

export function createInitialQuota(
  quotaId: string,
  parentWalletFundId?: number,
): EditableQuota {
  if (parentWalletFundId !== undefined) {
    return {
      id: quotaId,
      class: DEFAULT_FIC_CHILD_QUOTA.class,
      series: DEFAULT_FIC_CHILD_QUOTA.series,
      amount: DEFAULT_FIC_CHILD_QUOTA.amount,
    };
  }

  return {
    id: quotaId,
    class: '',
    series: '',
    amount: 0,
  };
}

function hasInvalidQuota(quota: EditableQuota): boolean {
  return !quota.class || !quota.series || quota.amount <= 0;
}

export function flattenWalletFunds(
  walletFunds: WalletFund[],
): EditableWalletFund[] {
  const funds: EditableWalletFund[] = [];
  const visitedWalletFunds = new Set<number>();

  const visit = (walletFund: WalletFund, parentWalletFundId?: number) => {
    if (visitedWalletFunds.has(walletFund.id)) return;

    visitedWalletFunds.add(walletFund.id);
    const resolvedParentWalletFundId =
      walletFund.parentWalletFund?.id ?? parentWalletFundId;

    funds.push({
      ...walletFund.operation,
      walletFundId: walletFund.id,
      parentWalletFundId: resolvedParentWalletFundId,
      compositionRole: walletFund.compositionRole ?? null,
      useAlertDefault: walletFund.useAlertDefault,
      quotas:
        walletFund.quotas?.map((quota, quotaIndex) => ({
          id: `quota-${walletFund.id}-${quotaIndex}`,
          class: quota.class,
          series: quota.series,
          amount: quota.amount,
        })) ?? [],
    });

    walletFund.childrenWalletFunds?.forEach((childWalletFund) =>
      visit(childWalletFund, walletFund.id),
    );
  };

  walletFunds.forEach((walletFund) => visit(walletFund));
  return funds;
}

export function groupFicWalletFunds(
  funds: EditableWalletFund[],
): EditableWalletFundNode[] {
  const childrenByParentId = new Map<number, EditableWalletFund[]>();

  funds.forEach((fund) => {
    if (fund.parentWalletFundId === undefined) return;

    const children = childrenByParentId.get(fund.parentWalletFundId) ?? [];
    children.push(fund);
    childrenByParentId.set(fund.parentWalletFundId, children);
  });

  const toNode = (fund: EditableWalletFund): EditableWalletFundNode => {
    const children = fund.walletFundId
      ? childrenByParentId.get(fund.walletFundId) ?? []
      : [];

    return {
      ...fund,
      nodeId: getWalletFundNodeId(fund),
      isFicParent:
        fund.compositionRole === 'fic_parent' ||
        (fund.compositionRole == null && children.length > 0),
      children: children.map(toNode),
    };
  };

  return funds
    .filter(
      (fund) =>
        fund.parentWalletFundId === undefined ||
        !funds.some(
          (candidate) => candidate.walletFundId === fund.parentWalletFundId,
        ),
    )
    .map(toNode);
}

export function getExpandedNodeIdsForInvalidQuotas(
  funds: EditableWalletFund[],
): string[] {
  const expanded = new Set<string>(['root']);

  const visit = (node: EditableWalletFundNode, parentNodeId?: string): void => {
    if (!node.isFicParent && node.quotas.some(hasInvalidQuota)) {
      expanded.add(node.nodeId);
      if (parentNodeId) {
        expanded.add(parentNodeId);
      }
    }

    node.children.forEach((child) => visit(child, node.nodeId));
  };

  groupFicWalletFunds(funds).forEach((node) => visit(node));

  return Array.from(expanded);
}

export function removeEditableWalletFund(
  funds: EditableWalletFund[],
  fundId: string,
): EditableWalletFund[] {
  const id = fundId.replace('fund-', '');
  const target = funds.find(
    (fund) =>
      fund.walletFundId?.toString() === id ||
      fund.editorId === id ||
      fund.hashId === id ||
      fund.id.toString() === id,
  );

  if (!target) return funds;

  const idsToRemove = new Set<number>();
  if (target.walletFundId !== undefined) {
    idsToRemove.add(target.walletFundId);

    let hasDescendantsToRemove = true;
    while (hasDescendantsToRemove) {
      hasDescendantsToRemove = false;

      funds.forEach((fund) => {
        if (
          fund.parentWalletFundId !== undefined &&
          idsToRemove.has(fund.parentWalletFundId) &&
          fund.walletFundId !== undefined &&
          !idsToRemove.has(fund.walletFundId)
        ) {
          idsToRemove.add(fund.walletFundId);
          hasDescendantsToRemove = true;
        }
      });
    }
  }

  return funds.filter(
    (fund) =>
      fund !== target &&
      !(
        fund.walletFundId !== undefined && idsToRemove.has(fund.walletFundId)
      ) &&
      !(
        fund.parentWalletFundId !== undefined &&
        idsToRemove.has(fund.parentWalletFundId)
      ),
  );
}

export function toUpdateWalletFunds(
  funds: EditableWalletFund[],
  confirmedFicParentWalletFundIds: number[] = [],
): UpdateWalletFundPayload[] {
  const ficParentWalletFundIds = new Set(confirmedFicParentWalletFundIds);

  funds.forEach((fund) => {
    if (fund.parentWalletFundId !== undefined) {
      ficParentWalletFundIds.add(fund.parentWalletFundId);
    }
  });

  return funds.map((fund) => {
    const isPersistedFicParent =
      fund.compositionRole === 'fic_parent' ||
      (fund.walletFundId !== undefined &&
        ficParentWalletFundIds.has(fund.walletFundId));
    const payload: UpdateWalletFundPayload = {
      operation: fund.id,
      useAlertDefault: fund.useAlertDefault,
      compositionRole: fund.compositionRole,
      quotas: isPersistedFicParent
        ? []
        : fund.quotas.map(({ class: quotaClass, series, amount }) => ({
            class: quotaClass,
            series,
            amount,
          })),
    };

    if (fund.parentWalletFundId !== undefined) {
      payload.parentWalletFundId = fund.parentWalletFundId;
    }

    return payload;
  });
}
